/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { CommonModule } from '@angular/common';
import { Component, Inject, Injectable } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_SNACK_BAR_DATA,
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarRef,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';

export type MessageType = 'info' | 'warn' | 'error';

interface SnackbarMessageData {
  title?: string;
  message: string;
  actionLabel: string;
}

@Component({
  selector: 'app-snackbar-message',
  imports: [CommonModule, MatButtonModule],
  template: `
    <div class="shared-snackbar-content">
      <div class="shared-snackbar-text">
        <div *ngIf="data.title" class="shared-snackbar-title">{{ data.title }}</div>
        <div class="shared-snackbar-message">{{ data.message }}</div>
      </div>
      <button mat-button type="button" class="shared-snackbar-action" (click)="triggerAction()">
        {{ data.actionLabel }}
      </button>
    </div>
  `,
  styles: [
    `
      .shared-snackbar-content {
        align-items: flex-start;
        color: white;
        display: flex;
        gap: 16px;
        justify-content: space-between;
        width: 100%;
      }

      .shared-snackbar-text {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
      }

      .shared-snackbar-title {
        font-size: 10px;
        font-weight: 700;
        line-height: 14px;
      }

      .shared-snackbar-message {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        overflow-wrap: anywhere;
        white-space: pre-line;
      }

      .shared-snackbar-action {
        align-self: center;
        color: white;
        flex-shrink: 0;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
      }
    `
  ]
})
class SnackbarMessageComponent {
  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: SnackbarMessageData,
    private snackBarRef: MatSnackBarRef<SnackbarMessageComponent>
  ) {}

  triggerAction(): void {
    this.snackBarRef.dismissWithAction();
  }
}

@Injectable({
  providedIn: 'root'
})
export class SnackbarService {
  duration: number = 10000;
  horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  constructor(private snackbar: MatSnackBar) {}

  showSnackBar(message: string, showButton: string = '', messageStatus: MessageType = 'info') {
    return this.snackbar.open(
      message,
      showButton.length === 0 ? $localize`:label for dismiss button@@dismiss:Dismiss` : showButton,
      {
        duration: this.duration,
        horizontalPosition: this.horizontalPosition,
        verticalPosition: this.verticalPosition,
        panelClass: ['shared-snackbar', `snackbar-${messageStatus}`]
      }
    );
  }

  showSnackBarWithTitle(
    title: string,
    message: string,
    showButton: string = '',
    messageStatus: MessageType = 'info'
  ) {
    return this.snackbar.openFromComponent(SnackbarMessageComponent, {
      data: {
        title,
        message,
        actionLabel:
          showButton.length === 0
            ? $localize`:label for dismiss button@@dismiss:Dismiss`
            : showButton
      },
      duration: this.duration,
      horizontalPosition: this.horizontalPosition,
      verticalPosition: this.verticalPosition,
      panelClass: ['shared-snackbar', `snackbar-${messageStatus}`]
    });
  }
}
